import useTitanApp from "@/hooks/useTitanApp";
import { useNavigate } from "react-router";
import { setPatientInfoTab } from "@/dispatchers/appDispatchers";
import {
  setFreeTypeMode,
  setIsFinalSummary,
  setPatientNotes,
  setSignatureDetails,
  setTranscript,
  setSelectedPatientConsultation,
  setisHandleStartConsultation,
  updateConsultationStatus,
  updateEventStatus,
  setPatientSummary,
  setDemDxSummary,
  setConsultationPaused,
  setPreConsultationTranscription,
  setPreConsultationTemplate,
  setPatientAIChatMessages,
  getPreConsultationByConsultationId,
  setReferralLetter,
  setVaccineDetails,
  setIncrementVaccineCompleted,
  setIncrementPrescriptionCompleted,
  setPrescriptionDetails,
  setIncrementReferralCompleted,
  setIncrementBookingCompleted,
  setIncrementVitalsCompleted,
  setVitalsDetails,
  setIncrementSignatureCompleted,
  setIncrementPaymentCompleted,
  getPaymentAuditByConsultation,
} from "@/dispatchers/patientDispatchers";

import {
  setHydratedTemplateSections,
  setIsTemplateSelected,
} from "@/dispatchers/templateDispatchers";
import { usePagination } from "@/components/ui/table-pagination";
import { setShowNHS111ReferralTab } from "@/dispatchers/pharmacyServiceDispatchers";

export interface Appointment {
  id: string;
  date: string; // DD/MM/YY
  time: string;
  type: string;
  status:
    | "Confirmed"
    | "Pending"
    | "Cancelled"
    | "Completed"
    | "Active"
    | "Rescheduled";
  practitionerName?: string;
  preConsultationTemplate?: any;
  preConsultationTemplateId?: string;
  patientConsultaitonId?: string;
  patientID?: string;
  eventStatus?: number;
  scheduleEventType?: string;
  resourceId?: string;
}

export const getTodaysAppointment = (
  appointments: Appointment[],
): Appointment | undefined => {
  const today = new Date().toLocaleDateString();
  return appointments.find((appointment) => {
    const appointmentDate = new Date(
      appointment.date.split("/").reverse().join("-"),
    ).toLocaleDateString();
    return appointmentDate === today;
  });
};

export const mapAppointments = (rawAppointments: any[]): Appointment[] => {
  if (!rawAppointments) return [];

  return rawAppointments.map((a) => {
    let parsedDate = "";
    try {
      const date = new Date(`${a.appointmentDate} 2025`);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      parsedDate = `${day}/${month}/${year}`;
    } catch {
      parsedDate = a.appointmentDate;
    }

    return {
      id: a.id,
      date: parsedDate,
      time: a.appointmentTime,
      type: a.serviceName || "General Consultation",
      status: (a.appointmentStatus as Appointment["status"]) || "Pending",
      practitionerName: a.practitionerName,
      preConsultationTemplate: a.preConsultationTemplate || null,
      preConsultationTemplateId: a.preConsultationTemplateId || null,
      patientConsultaitonId: a.patientConsultaitonId,
      patientID: a.patientID,
      eventStatus: a.eventStatus,
    };
  });
};

export const useAppointmentsState = () => {
  const { appState, patientState, templateState } = useTitanApp();
  const { currentPharmacy, currentPharmacyId } = appState || {};
  const {
    patientAppointmentList,
    patientAppointmentListLoader,
    selectedPatientDetails,
    patientActiveConsultation,
    preConsultData,
  } = patientState || {};
  const { isTemplateSelected } = templateState || {};
  const navigate = useNavigate();

  const appointments = mapAppointments(patientAppointmentList || []).filter(
    (a) => {
      if (a.eventStatus === 3 || a.eventStatus === 1) {
        return false;
      }

      const [day, month, year] = a.date.split("/");

      const fullYear =
        parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);

      const appointmentDate = new Date(
        fullYear,
        parseInt(month) - 1,
        parseInt(day),
      );

      appointmentDate.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return appointmentDate >= today;
    },
  );

  // Pagination setup - 5 items per page
  const pagination = usePagination(5);
  const { currentPage, itemsPerPage, handlePageChange, getPaginatedData } =
    pagination;

  // Calculate pagination
  const { paginatedData: paginatedAppointments, totalPages } =
    getPaginatedData(appointments);

  const handleStartConsultation = async (appointment: Appointment) => {
    setPatientSummary(null);
    setDemDxSummary(null);
    setTranscript("");
    setPatientNotes("");
    setHydratedTemplateSections(null);
    setSignatureDetails(null);
    setSelectedPatientConsultation(null);
    setisHandleStartConsultation(true);
    setIsFinalSummary(true);
    setConsultationPaused(false);
    setPatientAIChatMessages([]);
    setReferralLetter("");
    setVaccineDetails(null);
    setIncrementVaccineCompleted(false);
    setIncrementPrescriptionCompleted(false);
    setPrescriptionDetails(null);
    setIncrementReferralCompleted(false);
    setIncrementBookingCompleted(false);
    setIncrementVitalsCompleted(false);
    setVitalsDetails(null);
    setIncrementSignatureCompleted(false);
    setSignatureDetails(null);
    setIncrementPaymentCompleted(false);
    if (appointment?.preConsultationTemplate) {
      await getPreConsultationByConsultationId({
        consultationId: appointment?.patientConsultaitonId,
      });
    }
    await getPaymentAuditByConsultation({
      consultationID: appointment?.patientConsultaitonId,
    });
    setPreConsultationTemplate(preConsultData?.preConsultationTemplate);
    if (preConsultData?.preConsultationTemplate) {
      let preConsultTranscription = "";
      preConsultData?.preConsultationTemplate?.templateSections?.forEach(
        (section) => {
          section?.userControls?.forEach((control) => {
            if (control.label && control.value) {
              preConsultTranscription += ` ${control.label} ${control.value} `;
            }
          });
        },
      );

      setPreConsultationTranscription(preConsultTranscription.trim());
    } else {
      setPreConsultationTranscription("");
    }

    setShowNHS111ReferralTab(false);
    navigate(
      `/pharmacy/patientconsultation/${currentPharmacyId}/${appointment.patientID}/${appointment.patientConsultaitonId}`,
    );
    setPatientInfoTab(1);

    setIsTemplateSelected(true);
    const data = {
      consultationId: appointment.patientConsultaitonId,
      actionStatus: 2,
    };
    await updateConsultationStatus(data);

    const eventData = {
      id: appointment.id,
      eventStatus: 3,
    };
    await updateEventStatus(eventData);
  };

  return {
    appointments,
    paginatedAppointments,
    totalPages,
    currentPage,
    itemsPerPage,
    handlePageChange,
    patientAppointmentListLoader,
    selectedPatientDetails,
    handleStartConsultation,
  };
};
